import React, { ForwardedRef, forwardRef, PropsWithChildren, ReactNode } from 'react'

import * as DialogPrimitive from '@radix-ui/react-dialog'
import { RiCloseLine } from 'react-icons/ri'

import { useIntl } from '@app/intl/Intl'
import { bemJoin } from '@app/lib/bem'
import { useAppRoot } from '@app/lib/context/AppRoot'
import { IconButton } from '@app/lib/inputs/IconButton/IconButton'
import { Card } from '@app/lib/layout/Card/Card'
import { CardBlock } from '@app/lib/layout/Card/CardBlock'
import { CardHeader } from '@app/lib/layout/Card/CardHeader'
import { LineItem } from '@app/lib/layout/LineItem/LineItem'
import { MergeProps } from '@app/lib/types'

import './Dialog.scss'

const b = bemJoin('lawyer-dialog')

export const Dialog = DialogPrimitive.Root

export const DialogTrigger = forwardRef(
  ({ children }: PropsWithChildren<any>, ref: ForwardedRef<HTMLButtonElement>) => {
    return (
      <DialogPrimitive.Trigger asChild ref={ref}>
        {children}
      </DialogPrimitive.Trigger>
    )
  }
)

export type DialogContentProps = MergeProps<
  DialogPrimitive.DialogContentProps,
  {
    size?: 'sm' | 'md' | 'lg'
    title: ReactNode
  }
>

export const DialogContent = forwardRef(
  (
    { children, title, size = 'md', ...rest }: PropsWithChildren<DialogContentProps>,
    forwardedRef: ForwardedRef<HTMLDivElement>
  ) => {
    const { formatMessage } = useIntl()
    const { ref: appRootRef } = useAppRoot()

    return (
      <DialogPrimitive.Portal container={appRootRef.current}>
        <DialogPrimitive.Overlay className={b('backdrop')}>
          <DialogPrimitive.Content
            className={b('dialog', {
              [size]: true
            })}
            {...rest}
            ref={forwardedRef}
          >
            <Card className={b('card')}>
              <CardHeader className={b('header')}>
                <LineItem
                  label={<DialogPrimitive.Title>{title}</DialogPrimitive.Title>}
                  actions={
                    <DialogPrimitive.Close asChild>
                      <IconButton size='md' aria-label={formatMessage({ id: 'Lib.Dialog.Close' })}>
                        <RiCloseLine />
                      </IconButton>
                    </DialogPrimitive.Close>
                  }
                />
              </CardHeader>
              <DialogPrimitive.Description asChild>
                <CardBlock>{children}</CardBlock>
              </DialogPrimitive.Description>
            </Card>
          </DialogPrimitive.Content>
        </DialogPrimitive.Overlay>
      </DialogPrimitive.Portal>
    )
  }
)
